import { type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@eon/utils';
import { Button } from './button';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total pages (>=1). When < 2 the bar still renders to keep layout stable. */
  totalPages: number;
  /** Total items across all pages. */
  totalCount?: number;
  /** Current page size. */
  pageSize: number;
  /** Allowed page-size choices. Pass [] to hide the selector. */
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  /** Optional left-aligned slot (e.g. "Showing 1–50 of 100"). */
  summary?: ReactNode;
  className?: string;
  /** Visible state when waiting for next page. Disables controls. */
  loading?: boolean;
  labels?: {
    first?: string;
    previous?: string;
    next?: string;
    last?: string;
    page?: string;
    of?: string;
    rowsPerPage?: string;
    showing?: (from: number, to: number, total: number) => string;
  };
}

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

const DEFAULT_LABELS: Required<NonNullable<PaginationProps['labels']>> = {
  first: 'First page',
  previous: 'Previous page',
  next: 'Next page',
  last: 'Last page',
  page: 'Page',
  of: 'of',
  rowsPerPage: 'Rows per page',
  showing: (from, to, total) => `Showing ${from}–${to} of ${total}`,
};

/**
 * Pagination bar designed to pair with `DataTable`. Stateless: the host owns
 * page/pageSize state and reflects user intent via the change callbacks.
 */
export function Pagination({
  page,
  totalPages,
  totalCount,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  onPageChange,
  onPageSizeChange,
  summary,
  className,
  loading,
  labels,
}: PaginationProps) {
  const safeTotal = Math.max(1, totalPages);
  const current = Math.min(Math.max(1, page), safeTotal);
  const canPrev = current > 1 && !loading;
  const canNext = current < safeTotal && !loading;
  const l = { ...DEFAULT_LABELS, ...(labels ?? {}) };

  const from = totalCount && totalCount > 0 ? (current - 1) * pageSize + 1 : 0;
  const to = totalCount ? Math.min(current * pageSize, totalCount) : 0;

  return (
    <div
      className={cn(
        'text-muted-foreground mt-4 flex flex-col gap-3 text-xs sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        {summary ?? (
          <span aria-live="polite">
            {totalCount != null
              ? l.showing(from, to, totalCount)
              : `${l.page} ${current} ${l.of} ${safeTotal}`}
          </span>
        )}

        {pageSizeOptions.length > 0 && onPageSizeChange ? (
          <label className="flex items-center gap-2">
            <span className="hidden md:inline">{l.rowsPerPage}</span>
            <select
              value={pageSize}
              disabled={loading}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="bg-card border-input text-foreground focus-visible:ring-ring h-8 rounded-md border px-2 text-xs focus-visible:outline-none focus-visible:ring-2"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <nav className="flex items-center gap-1" aria-label="pagination">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label={l.first}
          disabled={!canPrev}
          onClick={() => onPageChange(1)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label={l.previous}
          disabled={!canPrev}
          onClick={() => onPageChange(current - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <span className="text-foreground px-2 font-medium tabular-nums">
          {l.page} {current} / {safeTotal}
        </span>

        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label={l.next}
          disabled={!canNext}
          onClick={() => onPageChange(current + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label={l.last}
          disabled={!canNext}
          onClick={() => onPageChange(safeTotal)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </nav>
    </div>
  );
}
